import { FAKE_ACCOUNT_ID } from "./fakeRuntime";
import { revalidateCapturedIntent } from "./authorizationComposition";

export const CONTROLLER_STATES = Object.freeze({
  CURRENT: "current",
  STALE: "stale",
  SUPERSEDED: "superseded",
  UNKNOWN: "unknown",
  INVALIDATED: "invalidated",
});

export const WORKSPACE_CONTROLLER_SCOPE = "agent-workspace-controller";

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
}

function isPositiveInteger(value) {
  return Number.isInteger(value) && value > 0;
}

export function createFakeControllerEvidence({
  controllerId,
  accountId = FAKE_ACCOUNT_ID,
  generation,
  actor = { kind: "fake", reference: "fake-actor-001" },
  state = CONTROLLER_STATES.CURRENT,
  observedAt = 0,
  supersededBy = null,
} = {}) {
  return deepFreeze({
    controllerId,
    accountId,
    generation,
    actor,
    state,
    observedAt,
    supersededBy,
  });
}

export const FAKE_CONTROLLER_EVIDENCE = deepFreeze({
  generationOneCurrent: createFakeControllerEvidence({
    controllerId: "fake-controller-001",
    generation: 1,
    observedAt: 1000,
  }),
  generationTwoCurrent: createFakeControllerEvidence({
    controllerId: "fake-controller-002",
    generation: 2,
    observedAt: 1100,
  }),
  missingGeneration: createFakeControllerEvidence({
    controllerId: "fake-controller-003",
    generation: undefined,
    state: CONTROLLER_STATES.UNKNOWN,
    observedAt: 1000,
  }),
  unknownController: createFakeControllerEvidence({
    controllerId: "fake-controller-004",
    generation: 1,
    state: CONTROLLER_STATES.UNKNOWN,
    observedAt: 1000,
  }),
  invalidatedAccountChanged: createFakeControllerEvidence({
    controllerId: "fake-controller-005",
    accountId: "fake-other-account-002",
    generation: 1,
    state: CONTROLLER_STATES.INVALIDATED,
    observedAt: 1000,
  }),
});

/**
 * Tracks the highest accepted fake controller generation per Account/scope.
 * Registration models fake acquisition/takeover: a strictly higher generation
 * supersedes the prior one; an equal or lower generation is never accepted,
 * so competing controllers can never both become current and a lower
 * generation can never regain current status.
 */
export function createControllerGenerationRegistry() {
  const currentByScope = new Map();

  function scopeKey(accountId, scope) {
    return `${accountId}::${scope}`;
  }

  function registerGeneration({
    accountId,
    scope = WORKSPACE_CONTROLLER_SCOPE,
    controllerId,
    generation,
  }) {
    if (!accountId || !isPositiveInteger(generation)) {
      return deepFreeze({ accepted: false, reason: "generation_invalid" });
    }
    const key = scopeKey(accountId, scope);
    const existing = currentByScope.get(key);
    if (existing && generation <= existing.generation) {
      return deepFreeze({
        accepted: false,
        reason: "generation_not_higher",
        currentGeneration: existing.generation,
        currentControllerId: existing.controllerId,
      });
    }
    currentByScope.set(key, { accountId, scope, controllerId, generation });
    return deepFreeze({ accepted: true, currentGeneration: generation, currentControllerId: controllerId });
  }

  function getCurrentGeneration({ accountId, scope = WORKSPACE_CONTROLLER_SCOPE }) {
    const existing = currentByScope.get(scopeKey(accountId, scope));
    if (!existing) return null;
    return deepFreeze({ generation: existing.generation, controllerId: existing.controllerId });
  }

  function reset() {
    currentByScope.clear();
  }

  return Object.freeze({ registerGeneration, getCurrentGeneration, reset });
}

/**
 * Pure fence check: given a snapshot of the registry's current generation,
 * decides whether presented controller evidence is current. Generation
 * bookkeeping (supersession) lives in the registry; this function never
 * mutates state and never grants currency from evidence alone, so
 * possessing a controller identity or a higher unregistered generation
 * number cannot itself establish ownership.
 */
export function evaluateControllerFence({ controllerEvidence, accountId, currentGeneration }) {
  if (!controllerEvidence || typeof controllerEvidence !== "object") {
    return deepFreeze({ valid: false, state: CONTROLLER_STATES.UNKNOWN, reason: "controller_missing" });
  }
  if (controllerEvidence.accountId !== accountId) {
    return deepFreeze({ valid: false, state: CONTROLLER_STATES.INVALIDATED, reason: "account_mismatch" });
  }
  if (!isPositiveInteger(controllerEvidence.generation)) {
    return deepFreeze({ valid: false, state: CONTROLLER_STATES.UNKNOWN, reason: "generation_invalid" });
  }
  if (controllerEvidence.state === CONTROLLER_STATES.UNKNOWN) {
    return deepFreeze({ valid: false, state: CONTROLLER_STATES.UNKNOWN, reason: "controller_unknown" });
  }
  if (controllerEvidence.state === CONTROLLER_STATES.INVALIDATED) {
    return deepFreeze({ valid: false, state: CONTROLLER_STATES.INVALIDATED, reason: "controller_invalidated" });
  }
  if (!currentGeneration || !isPositiveInteger(currentGeneration.generation)) {
    return deepFreeze({ valid: false, state: CONTROLLER_STATES.UNKNOWN, reason: "no_current_controller" });
  }
  if (controllerEvidence.generation < currentGeneration.generation) {
    return deepFreeze({ valid: false, state: CONTROLLER_STATES.STALE, reason: "generation_stale" });
  }
  if (controllerEvidence.generation > currentGeneration.generation) {
    return deepFreeze({ valid: false, state: CONTROLLER_STATES.UNKNOWN, reason: "generation_unregistered" });
  }
  if (controllerEvidence.controllerId !== currentGeneration.controllerId) {
    return deepFreeze({ valid: false, state: CONTROLLER_STATES.SUPERSEDED, reason: "controller_identity_mismatch" });
  }
  return deepFreeze({ valid: true, state: CONTROLLER_STATES.CURRENT, reason: null });
}

/**
 * Independently validates Account, actor, action, and resource agreement
 * across the intent, the current decision, and the presenting controller.
 * Account is checked first so a mismatch never discloses whether the
 * actor, action, or resource would otherwise have matched.
 */
export function evaluateAttemptMatch({ accountId, actor, action, resource, intent, decision, controllerEvidence }) {
  if (!intent || typeof intent !== "object") {
    return deepFreeze({ valid: false, reason: "intent_missing" });
  }
  if (
    intent.accountId !== accountId ||
    (decision && decision.accountId !== accountId) ||
    (controllerEvidence && controllerEvidence.accountId !== accountId)
  ) {
    return deepFreeze({ valid: false, reason: "account_mismatch" });
  }
  const actorReference = actor?.reference ?? null;
  if (
    !actorReference ||
    (decision && decision.actor?.reference !== actorReference) ||
    (controllerEvidence && controllerEvidence.actor?.reference !== actorReference)
  ) {
    return deepFreeze({ valid: false, reason: "actor_mismatch" });
  }
  if (intent.action !== action || (decision && decision.action !== action)) {
    return deepFreeze({ valid: false, reason: "action_mismatch" });
  }
  const resourceReference = resource?.reference ?? null;
  const intentResourceReference = intent.resource?.reference ?? null;
  const decisionResourceReference = decision?.resource?.reference ?? null;
  if (
    !resourceReference ||
    resourceReference !== intentResourceReference ||
    (decision && resourceReference !== decisionResourceReference)
  ) {
    return deepFreeze({ valid: false, reason: "resource_mismatch" });
  }
  return deepFreeze({ valid: true, reason: null });
}

/**
 * Deterministic attempt identity from stable logical inputs only. Retrying
 * the same logical attempt (same Account, actor, action, resource, intent,
 * decision, and revision) always yields the same identity; changing any
 * one of those inputs yields a distinct identity. Random values, wall
 * clock, controller generation, and provider identifiers are deliberately
 * excluded so a takeover or provider retry never changes attempt identity.
 */
export function computeAttemptIdentity({
  accountId,
  actorReference,
  action,
  resourceReference,
  intentId,
  decisionId,
  decisionRevision,
}) {
  if (
    !accountId ||
    !actorReference ||
    !action ||
    !resourceReference ||
    !intentId ||
    !decisionId ||
    !Number.isInteger(decisionRevision)
  ) {
    throw new Error(
      "computeAttemptIdentity requires accountId, actorReference, action, resourceReference, intentId, decisionId, and decisionRevision"
    );
  }
  return [
    "fake-attempt",
    accountId,
    actorReference,
    action,
    resourceReference,
    intentId,
    decisionId,
    decisionRevision,
  ].join("::");
}

/**
 * In-memory, resettable registry of admitted attempt outcomes. Only
 * admitted outcomes are recorded — recording performs no domain effect,
 * it only lets a repeated evaluation of the same logical attempt converge
 * on the previously admitted outcome instead of being treated as new work.
 */
export function createAttemptRegistry() {
  const outcomes = new Map();

  function getOutcome(attemptId) {
    return outcomes.get(attemptId) ?? null;
  }

  function recordOutcome(attemptId, outcome) {
    const existing = outcomes.get(attemptId);
    if (existing) return existing;
    const frozen = deepFreeze({ ...outcome, attemptId });
    outcomes.set(attemptId, frozen);
    return frozen;
  }

  function reset() {
    outcomes.clear();
  }

  return Object.freeze({ getOutcome, recordOutcome, reset });
}

/**
 * Evaluates one logical attempt to admit a captured semantic intent.
 * Authorization revalidation, controller fencing, and Account/actor/
 * action/resource matching are evaluated independently every call, so a
 * successful authorization decision can never bypass a stale or invalid
 * controller fence and a current controller can never bypass denied,
 * revoked, stale, Unknown, or mismatched authorization. Only when this
 * call's own live checks all pass is a prior converged outcome returned or
 * a new admission recorded; a presentation that is not currently valid is
 * always rejected fresh, even when an earlier presentation of the same
 * attempt identity was admitted.
 */
export function evaluateAttemptAdmission({
  accountId,
  actor,
  action,
  resource,
  intent,
  decision,
  controllerEvidence,
  controllerRegistry,
  attemptRegistry,
  logicalNow,
}) {
  const authorization = revalidateCapturedIntent({ intent, currentDecision: decision, accountId, logicalNow });
  const currentGeneration = controllerRegistry ? controllerRegistry.getCurrentGeneration({ accountId }) : null;
  const fence = evaluateControllerFence({ controllerEvidence, accountId, currentGeneration });
  const match = evaluateAttemptMatch({ accountId, actor, action, resource, intent, decision, controllerEvidence });

  const admitted = Boolean(authorization.admitted && fence.valid && match.valid);
  const reason = admitted ? null : !match.valid ? match.reason : !fence.valid ? fence.reason : authorization.reason;

  let attemptId = null;
  try {
    attemptId = computeAttemptIdentity({
      accountId,
      actorReference: actor?.reference,
      action,
      resourceReference: resource?.reference,
      intentId: intent?.intentId,
      decisionId: decision?.decisionId,
      decisionRevision: decision?.revision,
    });
  } catch (error) {
    attemptId = null;
  }

  if (!attemptId) {
    return deepFreeze({
      admitted: false,
      attemptId: null,
      duplicate: false,
      reason: "attempt_identity_unavailable",
      authorization,
      fence,
      match,
    });
  }

  const priorOutcome = attemptRegistry ? attemptRegistry.getOutcome(attemptId) : null;
  if (priorOutcome && admitted) {
    return priorOutcome;
  }

  const outcome = deepFreeze({
    admitted,
    attemptId,
    duplicate: Boolean(priorOutcome),
    reason,
    authorization,
    fence,
    match,
  });

  if (admitted && attemptRegistry) {
    return attemptRegistry.recordOutcome(attemptId, outcome);
  }
  return outcome;
}
